import { LIST_ROWS_PER_PAGE } from '../constants';
import axiosInstance from './api';
import { dedupePanelLoginRequest } from './panelLoginCache';
import { createFirebaseUser } from './firebaseUserProvisioning';
import { pickNotificationCount } from '../utils/notificationCount';

const getErrorMessage = (error, fallback) => error?.message || error?.error?.message || fallback;

export const normalizeAdminUser = (user = {}) => ({
  id: user.id ?? user.userId ?? user._id,
  uid: user.uid ?? user.firebaseUid ?? null,
  email: user.email ?? '',
  fullName: user.fullName ?? user.name ?? user.displayName ?? '',
  avatarUrl: user.avatarUrl ?? user.avatar ?? null,
  isAdmin: user.isAdmin,
  isSuperAdmin: user.isSuperAdmin,
  createdAt: user.createdAt ?? user.created_at,
  updatedAt: user.updatedAt ?? user.updated_at,
  notificationCount: pickNotificationCount(user),
});

/**
 * GET /admin/users
 * Supports { data: { users, total, limit, page } }, { data: users[] }, or a bare users array.
 */
export const normalizeAdminsListResponse = (payload, params = {}) => {
  const inner = payload?.data;
  const root = inner ?? payload;

  const list = Array.isArray(root) ? root : (root?.users ?? root?.admins ?? root?.adminUsers ?? []);

  const metaSource = Array.isArray(root) ? payload : root;

  const admins = list.map(normalizeAdminUser).filter((admin) => admin.id || admin.email);

  return {
    admins,
    total: Number(metaSource?.total ?? metaSource?.count ?? admins.length),
    limit: Number(metaSource?.limit ?? params.limit ?? LIST_ROWS_PER_PAGE),
    page: Number(metaSource?.page ?? params.page ?? 1),
  };
};

const requestAdminPanelLogin = async () => {
  try {
    const response = await axiosInstance.post('/admin/login');
    const payload = response.data;
    const session = payload?.data ?? {};
    const user = session?.user ?? payload?.data?.user;

    if (!user?.isAdmin) {
      return {
        success: false,
        message:
          payload?.message || 'Access denied. This account is not authorized for the admin panel.',
      };
    }

    const notificationCount = pickNotificationCount({ ...session, user });

    return {
      success: true,
      data: { ...normalizeAdminUser(user), notificationCount },
      session,
    };
  } catch (error) {
    return {
      success: false,
      message: getErrorMessage(error, 'Failed to verify admin access'),
      error,
    };
  }
};

/** POST /admin/login — after Firebase sign-in; 403 if not admin */
export const postAdminPanelLogin = (options = {}) =>
  dedupePanelLoginRequest('admin', requestAdminPanelLogin, options);

const getForgotPasswordErrorMessage = (error) => {
  const code = Number(error?.code ?? error?.status);
  const message = getErrorMessage(error, '');

  switch (code) {
    case 404:
      return message || 'No account found with this email address.';
    case 403:
      return message || 'This account is not authorized for this portal.';
    case 400:
      return message || 'Please enter a valid email address.';
    default:
      return message || 'Failed to verify email for password reset.';
  }
};

/**
 * POST /admin/forgot-password — no auth; verifies user before Firebase reset.
 * Backend uses the `role` field to distinguish admin vs partner.
 */
export const postAdminForgotPassword = async (email, role) => {
  try {
    const trimmedEmail = String(email ?? '').trim();
    if (!trimmedEmail) {
      return {
        success: false,
        message: 'Please enter your email address.',
      };
    }

    const response = await axiosInstance.post('/admin/forgot-password', {
      email: trimmedEmail,
      role,
    });
    const payload = response.data;
    const user = payload?.data ?? {};

    return {
      success: true,
      message: payload?.message || 'Account verified',
      data: user,
    };
  } catch (error) {
    return {
      success: false,
      message: getForgotPasswordErrorMessage(error),
      error,
    };
  }
};

export const listAdmins = async ({ limit = LIST_ROWS_PER_PAGE, page = 1 } = {}) => {
  try {
    const response = await axiosInstance.get('/admin/users', {
      params: { limit, page },
    });
    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    return {
      success: false,
      message: getErrorMessage(error, 'Failed to fetch admin users'),
      error,
    };
  }
};

/** POST /admin/users — grant admin access; body: { email, fullName, uid } */
const grantAdmin = async ({ email, fullName, uid }) => {
  try {
    const response = await axiosInstance.post('/admin/users', {
      email: email?.trim(),
      fullName: fullName?.trim(),
      uid: uid?.trim(),
    });
    const payload = response.data;

    return {
      success: true,
      data: payload?.data?.user ?? payload?.data ?? payload,
      message: payload?.message || 'Admin access granted',
      statusCode: response.status,
    };
  } catch (error) {
    return {
      success: false,
      message: getErrorMessage(error, 'Failed to add admin user'),
      error,
    };
  }
};

/** Create Firebase user, then grant admin via POST /admin/users */
export const provisionAndGrantAdmin = async ({ email, password, fullName }) => {
  const firebaseResult = await createFirebaseUser(email?.trim(), password);
  if (!firebaseResult.success) {
    return firebaseResult;
  }

  return grantAdmin({
    email: email?.trim(),
    fullName: fullName?.trim(),
    uid: firebaseResult.uid,
  });
};

/** PATCH /admin/users/:id — revokes admin access (isAdmin true → false); no body */
export const revokeAdmin = async (userId) => {
  try {
    const response = await axiosInstance.patch(`/admin/users/${userId}`);
    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    return {
      success: false,
      message: getErrorMessage(error, 'Failed to revoke admin access'),
      error,
    };
  }
};
